import React, { useEffect, useState, useCallback } from "react";
import styled, { ThemeProvider } from "styled-components";

import { toast, Flip } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { SIZE, SIZE_MEDIUM, isFontSize } from "constants/themes";
import { MESSAGE_UPDATED, POSITION_RELOAD } from "constants/webview";

import {
  STATUS_PICKUP,
  STATUS_DELIVERY_WAITING,
  STATUS_DELIVERY_POSTPONED,
  STATUS_DELIVERY_STARTED,
  STATUS_CS,
  DELIVERY_TYPE_NORMAL,
  DELIVERY_TYPE_PICKUP,
  DELIVERY_TYPE_RETURN,
} from "Models";

import {
  useDeliveriesStore,
  useOrderStore,
  useMapStore,
  useReallocationStore,
  useRoutesStore,
  useModalStore,
} from "stores/hooks";

import { getOrder, getRearrange } from "utils/common";
import { initMap } from "utils/map";
import { appVersion, defaultSize } from "utils/params";

import BottomSummary from "components/Map/BottomSummary";
import BottomButtons from "components/Map/BottomButtons";
import CountSummary from "components/Map/CountSummary";
import OrderGuide from "components/Map/OrderGuide";
import RearrangeGuide from "components/Map/RearrangeGuide";
import TrackingMarkers from "components/Map/TrackingMarkers";
import RouteInfo from "components/Map/RouteInfo";

import OrderConfirmModal from "modals/OrderConfirmModal";
import OrderAutoConfirmModal from "modals/OrderAutoConfirmModal";
import ResetConfirmModal from "modals/ResetConfirmModal";
import RidersModal from "modals/RidersModal";
import SettingModal from "modals/SettingModal";

import { TMAP } from "constants/links";

import { checkNearbyMarkers } from "utils/deliveries";

import { ReactComponent as IconCurrent } from "assets/images/map/icon-current.svg";
import { ReactComponent as IconRefresh } from "assets/images/map/icon-refresh.svg";
import { ReactComponent as IconSetting } from "assets/images/map/icon-setting.svg";
import * as mapImages from "assets/images/map/urls";

const MapContainer = styled.div`
  .info-title {
    font-weight: bold;
    font-size: ${({ theme }) => theme.font16}px;
    display: flex;
    flex-wrap: wrap;
  }

  .info-spot {
    font-weight: bold;
    font-size: ${({ theme }) => theme.font14}px;
  }

  .info-base-address {
    font-weight: bold;
    font-size: ${({ theme }) => theme.font14}px;
  }

  .info-address {
    color: #828182;
    font-size: ${({ theme }) => theme.font14}px;
  }

  .info-address.bold {
    font-weight: 700;
    color: #000000;
  }

  .info-button-wrap {
    display: flex;
    align-items: center;
    position: absolute;
    bottom: 0;
    width: 100%;
    background-color: #fff;
    z-index: 10;
  }

  .btn {
    flex: 1;
    background-color: #2bd723;
    font-size: 16px;
    padding: 7px 10px;
    color: #fff;
    width: 100%;
    border-radius: 0;
  }

  .btn.btn-border {
    background-color: transparent;
    border: 1px solid #2bd723;
    color: #2bd723;
  }

  .marker-address {
    font-size: ${({ theme }) => theme.font10}px;
    position: absolute;
    width: 80px;
    text-align: center;
    top: 40px;
    padding: 3px;
    background-color: rgba(0, 0, 0, 0.1);
  }
`;

const Button = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background-color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1.7px 4px 0 rgba(0, 0, 0, 0.2);
`;

// 현재 위치 조회
const ButtonCurrent = styled(Button)`
  svg {
    width: 24px;
    height: 24px;
  }
`;

// 새로고침
const ButtonReload = styled(Button)`
  svg {
    width: 16px;
    height: 16px;
  }
`;

// 설정
const ButtonSetting = styled(Button)`
  svg {
    width: 22px;
    height: 22px;
  }
`;

// 지도
const MapConatiner = styled.div`
  width: 100%;
  height: 100vh;
`;

const TopWrap = styled.div`
  position: absolute;
  top: 10px;
  z-index: 1000;
  width: 100%;
  background: rgba(0, 0, 0, 0.15);
  padding: 6px 0;
  ${({ hide }) => hide && `display: none;`};
`;

const BottomWrap = styled.div`
  position: absolute;
  bottom: 12px;
  z-index: 1000;
  width: 100%;

  ${({ hide }) => hide && `display: none;`}
`;

const BottomButtonsWrap = styled.div`
  padding-left: 12px;
  padding-right: 12px;
  display: flex;
  justify-content: space-between;
`;

let markers = [];
let polylines = [];
let markerCompleted = null;
let infoWindows = [];

toast.configure({
  transition: Flip,
  autoClose: 1000,
  draggable: false,
  closeButton: false,
});

function MapWithRoutes() {
  const {
    count,

    statuses,

    markerList,
    markerListWithSameplace,
    deliveryStartList,
    orderList,

    fetched,

    deliveriesAllocated,

    limitedHoursCount,
  } = useDeliveriesStore().state;
  const { ...deliveriesActions } = useDeliveriesStore();

  const { isOrderMode, newOrderList } = useOrderStore().state;
  const { ...orderActions } = useOrderStore();

  const { isRearrangeMode, selectedDeliveries } = useReallocationStore().state;
  const { ...reallocationActions } = useReallocationStore();

  const { route, routesViaStops, delvieryCompleted } = useRoutesStore().state;
  const { ...routesActions } = useRoutesStore();

  const { map, showHideMarkers, showMarkerAddress, currentCenter, linkOthers } =
    useMapStore().state;
  const { ...mapActions } = useMapStore();

  const { modals } = useModalStore().state;
  const { openModal, closeModal } = useModalStore();

  const [reload, setReload] = useState(false);

  // 폰트 사이즈
  const [size, setSize] = useState(defaultSize || SIZE_MEDIUM);

  const [currentPosition, setCurrentPosition] = useState(null);

  const handleChangeReload = useCallback((_reload = true) => {
    setReload(_reload);
  }, []);

  useEffect(() => {
    fetchAll();
    // fetchAllocated();
    fetchCount();
    fetchLimitedHoursCount();

    fetchRoutesStposByLocalStorage();

    handleChangeReload(false);
  }, [statuses, reload]);

  const fetchAll = useCallback(async () => {
    try {
      await deliveriesActions.fetchAll();
    } catch (e) {
      console.log(e);
    }
  }, []);

  // const fetchAllocated = useCallback(async () => {
  //   try {
  //     await deliveriesActions.fetchAllocated();
  //   } catch(e) {
  //     console.log(e)
  //   }
  // }, []);

  const fetchCount = useCallback(async () => {
    try {
      await deliveriesActions.fetchCount();
    } catch (e) {
      console.log(e);
    }
  }, []);

  const fetchLimitedHoursCount = useCallback(async () => {
    try {
      await deliveriesActions.fetchLimitedHoursCount();
    } catch (e) {
      console.log(e);
    }
  }, []);

  const fetchRoutesStposByLocalStorage = useCallback(async () => {
    try {
      await routesActions.fetchRoutesStposByLocalStorage();
    } catch (e) {
      console.log(e);
    }
  }, []);

  const reloadDeliveries = useCallback(() => {
    handleChangeReload(true);
  }, []);

  // 즐겨찾기 라이더
  const [favoRiders, setFavoRiders] = useState([]);

  const receiveMessage = useCallback(
    (e) => {
      if (e.data) {
        if (e.data === POSITION_RELOAD) {
          handleChangeReload(true);
        } else if (isFontSize(e.data)) {
          setSize(e.data);
        } else {
          const message = JSON.parse(e.data);
          if (message.type === "favoRiders") {
            setFavoRiders(message.favoRiders);
          } else {
            mapActions.updateCurrentPostion(message);
            setCurrentPosition(message);
          }
        }
      }
    },
    [mapActions.updateCurrentPostion],
  );

  useEffect(() => {
    initMap(() => {
      mapActions.drawMap({ zoom: 15 });
    });
  }, [mapActions.drawMap]);

  useEffect(() => {
    setTimeout(() => {
      document.addEventListener("message", receiveMessage);
    }, 200);

    return () => {
      document.removeEventListener("message", receiveMessage);
    };
  }, [receiveMessage]);

  const handleClickStartRearrange = useCallback(() => {
    handleChangeReload(true);
    reallocationActions.startRearrangeMode();
  }, [reallocationActions.startRearrangeMode]);

  const handleClickCancelRearrangeMode = useCallback(() => {
    if (
      window.confirm(
        "배차전환 취소하시겠습니까? 취소 시 현재까지 선택된 마커가 모두 삭제됩니다.",
      )
    ) {
      handleChangeReload(true);
      reallocationActions.cancelRearrangeMode();
    }
  }, [reallocationActions.cancelRearrangeMode]);

  const selectDelivery = useCallback(
    ({
      bookId,
      status,
      statusString,
      statusRearrangeKey,
      samePlaces,
      reallocatedRiderId,
    }) => {
      if (
        selectedDeliveries.length > 0 &&
        selectedDeliveries[0].status !== status
      ) {
        toast(`${selectedDeliveries[0].statusString}건만 체크해주세요`, {
          className: "toast-background",
          bodyClassName: "toast-body",
          progressClassName: "fancy-progress-bar",
        });
      } else {
        reallocationActions.selectDelivery({
          bookId,
          status,
          statusString,
          statusRearrangeKey,
          samePlaces,
          reallocatedRiderId,
        });
      }
    },
    [reallocationActions.selectDelivery, selectedDeliveries],
  );

  // 배송순서 지정
  const handleClickSelectOrderAuto = useCallback(() => {
    openModal(
      <OrderAutoConfirmModal
        key="order-auto-confirm-modal"
        startOrderManual={handleClickStartOrder}
        currentPosition={currentPosition}
        onUpdate={handleChangeReload}
      />,
    );
  }, [currentPosition]);

  const handleClickStartOrder = useCallback(() => {
    closeModal();

    if (orderList.length > 0) {
      // 이미 순서 지정이 되어 있을 경우
      openModal(
        <ResetConfirmModal
          key="reset-confirm-modal"
          startOrder={setOrderMode}
        />,
      );
    } else {
      setOrderMode();
    }
  }, [orderList]);

  const setOrderMode = useCallback(() => {
    if (
      statuses.includes(STATUS_DELIVERY_WAITING) &&
      statuses.includes(STATUS_DELIVERY_POSTPONED) &&
      statuses.includes(STATUS_DELIVERY_STARTED)
    ) {
      handleChangeReload(true);
    } else {
      deliveriesActions.setStatuses([
        STATUS_DELIVERY_WAITING,
        STATUS_DELIVERY_POSTPONED,
        STATUS_DELIVERY_STARTED,
      ]);
    }

    orderActions.startOrderMode();

    closeModal();
  }, [deliveriesActions.setStatuses, orderActions.startOrderMode, statuses]);

  const selectOrderDelivery = useCallback(
    ({ bookId, status, receiverAddress, samePlaces }) => {
      if (status === STATUS_DELIVERY_STARTED) {
        orderActions.selectOrderDelivery({
          bookId,
          receiverAddress,
          samePlaces,
        });
      } else {
        toast(`배송 건만 체크해주세요`, {
          className: "toast-background",
          bodyClassName: "toast-body",
          progressClassName: "fancy-progress-bar",
        });
      }
    },
    [orderActions.selectOrderDelivery],
  );

  const handleClickCancelOrderMode = useCallback(() => {
    if (
      window.confirm(
        "순서지정을 취소하시겠습니까? 취소 시 현재까지 지정한 순서가 모두 삭제됩니다.",
      )
    ) {
      cancelOrderMode();
    }
  }, []);

  const cancelOrderMode = useCallback(() => {
    orderActions.cancelOrderMode();

    closeModal();

    deliveriesActions.resetStatuses();

    handleChangeReload(true);
  }, [deliveriesActions.resetStatuses, orderActions.cancelOrderMode]);

  // 마커 클릭 핸들러
  const getClickHandler = useCallback(
    (seq) => {
      return function () {
        var marker = markers[seq],
          infoWindow = infoWindows[seq];

        if (infoWindow.getMap()) {
          infoWindow.close();
        } else {
          if (isOrderMode) {
            selectOrderDelivery({
              bookId: marker.bookId,
              status: marker.status,
              receiverAddress:
                marker.receiverAddress || marker.receiverAddressRoad || "",
              samePlaces: marker.samePlaces,
            });
          } else if (isRearrangeMode) {
            selectDelivery({
              bookId: marker.bookId,
              status: marker.status,
              statusString: marker.statusString,
              statusRearrangeKey: marker.statusRearrangeKey,
              samePlaces: marker.samePlaces,
              reallocatedRiderId: marker.reallocatedRiderId,
            });
          } else {
            infoWindow.open(map, marker);
          }
        }
      };
    },
    [map, isOrderMode, isRearrangeMode, selectOrderDelivery, selectDelivery],
  );

  // 지도 영역 밖 마커
  const [hideMarkers, setHideMarkers] = useState([]);

  const updateMarkers = useCallback((map, markers) => {
    let mapBounds = map.getBounds();
    let marker, position;

    let _hiderMarkers = [];

    for (var i = 0; i < markers.length; i++) {
      marker = markers[i];
      position = marker.getPosition();

      if (mapBounds.hasLatLng(position)) {
        showMarker(map, marker);
      } else {
        hideMarker(marker);
        _hiderMarkers.push(marker);
      }
    }

    setHideMarkers(_hiderMarkers);
  }, []);

  const drawMarker = useCallback(() => {
    for (let i = 0; i < markers.length; i++) {
      markers[i].setMap(null);

      var infoWindow = infoWindows[i];

      if (infoWindow.getMap()) {
        infoWindow.close();
      }
    }

    markers = [];
    infoWindows = [];

    if (markerList.length > 0) {
      markerList.forEach((item) => {
        // 마커 옆 아이콘
        let isHighPrice = false;
        let isLimitedHours = false;
        let isReallocated = false;
        let isCustomerAddressEng = false;
        let receiverWaitDeliveriesCount = 0;
        let receiverStartedDeliveriesCount = 0;
        let receiverPostponedDeliveriesCount = 0;
        let isNearbyMarker = false;
        let isNearbyMarkerInOtherStatus = false;

        // 같은 위치에 마커가 있을 경우 마커를 표시하지 않는다.
        // const prevMarkerIndex = markers.findIndex(
        //   (d) =>
        //     d.title !== item.bookId &&
        //     d.position.y === Number(item.latitude) &&
        //     d.position.x === Number(item.longitude),
        // );

        // if (prevMarkerIndex > -1) {
        //   markers.splice(prevMarkerIndex, 1);
        //   infoWindows.splice(prevMarkerIndex, 1);
        // }

        if (item.isHighPrice) {
          isHighPrice = true;
        }
        if (item.isLimitedHours) {
          isLimitedHours = true;
        }
        if (item.reallocated) {
          isReallocated = true;
        }
        if (item.customerAddressEng) {
          isCustomerAddressEng = true;
        }

        // 수거일경우 동일 주소 툴팁 표시 제외
        let pickupInfoWindowDone = false;

        // 상세 페이지 이동
        let linkingDetail = `
            window.ReactNativeWebView.postMessage('${JSON.stringify({
              type: "detail",
              bookId: item.bookId,
            })}');
          `;

        let titleAddressRoad = "";
        if (
          item.status === STATUS_PICKUP &&
          item.senderAddressRoadWithoutSigungu
        ) {
          titleAddressRoad = item.senderAddressRoadWithoutSigungu.replace(
            / /g,
            "",
          );
        } else if (
          item.status === STATUS_PICKUP &&
          item.senderAddressWithoutSigungu
        ) {
          titleAddressRoad = item.senderAddressWithoutSigungu.replace(/ /g, "");
        } else if (
          item.status !== STATUS_PICKUP &&
          item.receiverAddressRoadWithoutSigungu
        ) {
          titleAddressRoad = item.receiverAddressRoadWithoutSigungu.replace(
            / /g,
            "",
          );
        } else if (
          item.status !== STATUS_PICKUP &&
          item.receiverAddressWithoutSigungu
        ) {
          titleAddressRoad = item.receiverAddressWithoutSigungu.replace(
            / /g,
            "",
          );
        }

        let linkingOtherApp = `
            window.ReactNativeWebView.postMessage('${JSON.stringify({
              type: "linking",
              app: linkOthers,
              longitude: item.longitude,
              latitude: item.latitude,
              title: `${
                item.order ? `(${item.order})` : ""
              }${titleAddressRoad}`,
            })}');
          `;

        let linkingOtherAppRoute = `
            window.ReactNativeWebView.postMessage('${JSON.stringify({
              type: "route",
              app: linkOthers,
              longitude: item.longitude,
              latitude: item.latitude,
              title:
                (item.renderSenderName
                  ? item.renderSenderName.replace(/\//g, "").replace(/ /g, "")
                  : "") +
                (item.renderReceiverName
                  ? item.renderReceiverName.replace(/\//g, "").replace(/ /g, "")
                  : ""),
            })}');
          `;

        if (!appVersion) {
          linkingDetail = `window.ReactNativeWebView.postMessage('${item.bookId}');`;
        }

        // 동일수 하인 동일 주소의 개수
        let receiverWaitDeliveriesArr =
          item.sameReceiverDeliveriesObj.waiting || [];
        let receiverStartedDeliveriesArr =
          item.sameReceiverDeliveriesObj.started || [];
        let receiverPostponedDeliveriesArr =
          item.sameReceiverDeliveriesObj.postponed || [];

        if (
          (item.status === STATUS_DELIVERY_WAITING ||
            item.status === STATUS_DELIVERY_POSTPONED ||
            item.status === STATUS_DELIVERY_STARTED) &&
          deliveriesAllocated &&
          deliveriesAllocated.length > 0
        ) {
          receiverWaitDeliveriesCount = receiverWaitDeliveriesArr.length;
          receiverStartedDeliveriesCount = receiverStartedDeliveriesArr.length;
          receiverPostponedDeliveriesCount =
            receiverPostponedDeliveriesArr.length;
        }

        // 근접 마커 여부 표시
        const { nearbyMarkersArr, nearbyMarkersInOtherStatusArr } =
          checkNearbyMarkers(item, markerListWithSameplace);
        if (nearbyMarkersArr.length > 0) {
          isNearbyMarker = true;
        } else {
          isNearbyMarker = false;
        }

        // 근접 마커 여부 표시(다른 상태값)
        if (nearbyMarkersInOtherStatusArr.length > 0) {
          isNearbyMarkerInOtherStatus = true;
        } else {
          isNearbyMarkerInOtherStatus = false;
        }

        // 툴팁 내용
        let infoWindowContent = ``;

        let orderingNumbersByDeliveryRiderId = [];
        let orderingNumberByDeliveryRiderId = "";

        if (item.orderingNumberByDeliveryRiderId) {
          orderingNumbersByDeliveryRiderId.push(
            item.orderingNumberByDeliveryRiderId,
          );

          orderingNumberByDeliveryRiderId = ` [${item.orderingNumberByDeliveryRiderId}]`;
        }

        if (appVersion) {
          if (linkOthers === TMAP) {
            infoWindowContent += `
                <div class="info-button-wrap">
                  <button type="button" class="btn" onclick=${linkingOtherAppRoute}">경로 찾기</button>
                </div>
              `;
          } else {
            infoWindowContent += `
              <div class="info-button-wrap">
                <button type="button" class="btn btn-border" onclick=${linkingOtherApp}">위치 찾기</button>
                <button type="button" class="btn" onclick=${linkingOtherAppRoute}">경로 찾기</button>
              </div>
            `;
          }
        }

        infoWindowContent += `
            <div style="max-height: 278px; overflow: auto; padding-bottom: 40px; position: relative;">
              <div 
                class="info ${item.statusText} ${item.keyColor}" 
                style="padding: 10px; max-width: 250px" 
                onclick=${linkingDetail}
              >
                ${
                  item.status === STATUS_PICKUP
                    ? ""
                    : `
                  <div class="info-icons" style="margin-top: -5px;">
                    <div class="info-icon highPrice ${
                      !item.isHighPrice && "disabled"
                    }">
                      <img style="width: 6px;" src="${mapImages.iconDollar}" />
                    </div>
                    <div class="info-icon time ${
                      !item.isLimitedHours && "disabled"
                    }">
                      <img style="width: 6px;" src="${mapImages.iconClock}" />
                    </div>
                    <div class="info-icon rearrange ${
                      !item.reallocated && "disabled"
                    }">
                      <img style="width: 8px;" src="${mapImages.iconRotation}" />
                    </div>
                    <div class="info-icon deliveryWait ${
                      receiverWaitDeliveriesArr.length === 0 && "disabled"
                    }">
                      ${receiverWaitDeliveriesArr.length}
                    </div>
                    <div class="info-icon deliveryStart ${
                      receiverStartedDeliveriesArr.length === 0 && "disabled"
                    }">
                      ${receiverStartedDeliveriesArr.length}
                    </div>
                    <div class="info-icon deliveryPostponed ${
                      receiverPostponedDeliveriesArr.length === 0 && "disabled"
                    }">
                      <img style="width: 7px;" src="${mapImages.iconPostponed}" />
                    </div>
                  </div>
                `
                }
                <div class="info-title ${item.statusText}">
                  <span style="color: ${item.keyColorHex}">
                    (${item.statusString})${orderingNumberByDeliveryRiderId}
                  </span>&nbsp;
                  ${item.bookId}&nbsp;
                  <span style="color: ${item.keyColorHex}">
                    ${item.typeString}
                  </span>
                </div>
                <div class="info-spot">
                  ${
                    item.status === STATUS_PICKUP
                      ? `${item.spotName} ${
                          item.spot ? `(${item.renderSenderName})` : ""
                        }`
                      : `${item.renderReceiverName}(${item.renderSenderName})`
                  }
                  <span class="c-red">
                    ${
                      item.status !== STATUS_PICKUP && item.customerAddressEng
                        ? " (영어주소)"
                        : ""
                    }
                  </span>
                </div>
                <div class="info-base-address">
                ${
                  item.type === DELIVERY_TYPE_NORMAL
                    ? `${item.displayReceiverBuildingDongNumber || "없음"}(${
                        item.renderAddressApiType
                      })`
                    : item.type !== DELIVERY_TYPE_NORMAL
                      ? `${item.displaySenderBuildingDongNumber || "없음"}(${
                          item.renderAddressApiType
                        })`
                      : ""
                } 
                ${
                  item.frontdoorPassword ? ` / ${item.frontdoorPassword}` : ""
                }        
                </div>
                <div class="info-address">
                  ${
                    item.status === STATUS_PICKUP
                      ? `(출) ${
                          item.senderAddress || item.senderAddressRoad || ""
                        }, ${item.senderAddressDetail}`
                      : `(도) ${item.displayAddress} ${
                          item.memoFromCustomer
                            ? `<span class="c-red">(${item.memoFromCustomer})</span>`
                            : ""
                        }`
                  }
                </div>
                <div class="info-address bold">
                  ${
                    item.status !== STATUS_PICKUP && item.customerAddressEngGpt
                      ? `(GPT번역) ${item.customerAddressEngGpt}`
                      : ""
                  }
                </div>
                <div class="info-address">
                  ${
                    item.status === STATUS_PICKUP
                      ? `${
                          item.senderAddressRoadWithoutSigungu ||
                          item.senderAddressWithoutSigungu
                        } ${
                          item.senderAddressObj &&
                          item.senderAddressObj.building
                            ? ` ${item.senderAddressObj.building}`
                            : ""
                        }`
                      : `${
                          item.receiverAddressRoadWithoutSigungu ||
                          item.receiverAddressWithoutSigungu
                        }${
                          item.receiverAddressObj &&
                          item.receiverAddressObj.building
                            ? ` ${item.receiverAddressObj.building}`
                            : ""
                        }${
                          item.receiverAddressRoadWithoutSigungu &&
                          item.senderAddressWithoutSigungu
                            ? `/${item.senderAddressWithoutSigungu}`
                            : ""
                        }${
                          !item.receiverAddressRoadWithoutSigungu &&
                          item.receiverDong
                            ? `/${item.receiverDong}`
                            : ""
                        }`
                  }
                </div>
              </div>
          `;

        if (
          item.status === STATUS_PICKUP &&
          item.type === DELIVERY_TYPE_NORMAL
        ) {
          pickupInfoWindowDone = true;
        }

        // 툴팁 : 동일 주소 목록
        if (item.samePlaces && item.samePlaces.length > 0) {
          item.samePlaces.forEach((same) => {
            if (
              same.status === STATUS_DELIVERY_WAITING ||
              same.status === STATUS_DELIVERY_POSTPONED ||
              same.status === STATUS_DELIVERY_STARTED ||
              same.type === DELIVERY_TYPE_PICKUP ||
              same.type === DELIVERY_TYPE_RETURN ||
              !pickupInfoWindowDone
            ) {
              if (same.isHighPrice) {
                isHighPrice = true;
              }
              if (same.isLimitedHours) {
                isLimitedHours = true;
              }
              if (same.reallocated) {
                isReallocated = true;
              }
              if (same.customerAddressEng) {
                isCustomerAddressEng = true;
              }

              let linkingDetail = `
                  window.ReactNativeWebView.postMessage('${JSON.stringify({
                    type: "detail",
                    bookId: same.bookId,
                  })}');
                `;

              if (!appVersion) {
                linkingDetail = `window.ReactNativeWebView.postMessage('${same.bookId}');`;
              }

              let sameReceiverWaitDeliveriesArr =
                same.sameReceiverDeliveriesObj?.waiting || [];
              let sameReceiverStartedDeliveriesArr =
                same.sameReceiverDeliveriesObj?.started || [];
              let sameReceiverPostponedDeliveriesArr =
                same.sameReceiverDeliveriesObj?.postponed || [];

              if (
                (same.status === STATUS_DELIVERY_WAITING ||
                  same.status === STATUS_DELIVERY_POSTPONED ||
                  same.status === STATUS_DELIVERY_STARTED) &&
                deliveriesAllocated &&
                deliveriesAllocated.length > 0
              ) {
                if (
                  sameReceiverWaitDeliveriesArr.length >
                  receiverWaitDeliveriesCount
                ) {
                  receiverWaitDeliveriesCount =
                    sameReceiverWaitDeliveriesArr.length;
                }
                if (
                  sameReceiverStartedDeliveriesArr.length >
                  receiverStartedDeliveriesCount
                ) {
                  receiverStartedDeliveriesCount =
                    sameReceiverStartedDeliveriesArr.length;
                }
                if (
                  sameReceiverPostponedDeliveriesArr.length >
                  receiverPostponedDeliveriesCount
                ) {
                  receiverPostponedDeliveriesCount =
                    sameReceiverPostponedDeliveriesArr.length;
                }
              }

              let sameOrderingNumberByDeliveryRiderId = "";

              if (same.orderingNumberByDeliveryRiderId) {
                orderingNumbersByDeliveryRiderId.push(
                  same.orderingNumberByDeliveryRiderId,
                );

                sameOrderingNumberByDeliveryRiderId = ` [${same.orderingNumberByDeliveryRiderId}]`;
              }

              infoWindowContent += `
                  <div 
                    class="info ${same.statusText} ${same.keyColor}" 
                    style="padding: 5px 10px 10px; max-width: 250px" 
                    onclick=${linkingDetail}
                  >
                    ${
                      same.status === STATUS_PICKUP
                        ? ""
                        : `
                      <div class="info-icons">
                        <div class="info-icon highPrice ${
                          !same.isHighPrice && "disabled"
                        }">
                          <img style="width: 6px;" src="${mapImages.iconDollar}" />
                        </div>
                        <div class="info-icon time ${
                          !same.isLimitedHours && "disabled"
                        }">
                          <img style="width: 6px;" src="${mapImages.iconClock}" />
                        </div>
                        <div class="info-icon rearrange ${
                          !same.reallocated && "disabled"
                        }">
                          <img style="width: 8px;" src="${mapImages.iconRotation}" />
                        </div>
                        <div class="info-icon deliveryWait ${
                          sameReceiverWaitDeliveriesArr.length === 0 &&
                          "disabled"
                        }">
                          ${sameReceiverWaitDeliveriesArr.length}
                        </div>
                        <div class="info-icon deliveryStart ${
                          sameReceiverStartedDeliveriesArr.length === 0 &&
                          "disabled"
                        }">
                          ${sameReceiverStartedDeliveriesArr.length}
                        </div>
                        <div class="info-icon deliveryPostponed ${
                          sameReceiverPostponedDeliveriesArr.length === 0 &&
                          "disabled"
                        }">
                          <img style="width: 7px;" src="${mapImages.iconPostponed}" />
                        </div>
                      </div>
                    `
                    }
                    <div class="info-title ${same.statusText}">
                      <span style="color: ${same.keyColorHex}">(${
                        same.statusString
                      })${sameOrderingNumberByDeliveryRiderId}</span>&nbsp;
                      ${same.bookId}&nbsp;
                      <span style="color: ${same.keyColorHex}">${
                        same.typeString
                      }</span>
                    </div>
                    <div class="info-spot">
                      ${
                        same.status === STATUS_PICKUP
                          ? `${same.spotName} ${
                              same.spot ? `(${same.renderSenderName})` : ""
                            }`
                          : `${same.renderReceiverName}(${same.renderSenderName})`
                      }
                      <span class="c-red">
                        ${
                          same.status !== STATUS_PICKUP &&
                          same.customerAddressEng
                            ? " (영어주소)"
                            : ""
                        }
                      </span>
                    </div>
                    <div class="info-base-address">
                      ${
                        same.type === DELIVERY_TYPE_NORMAL
                          ? `${
                              same.displayReceiverBuildingDongNumber || "없음"
                            }(${same.renderAddressApiType})`
                          : same.type !== DELIVERY_TYPE_NORMAL
                            ? `${
                                same.displaySenderBuildingDongNumber || "없음"
                              }(${same.renderAddressApiType})`
                            : ""
                      } 
                      ${
                        same.frontdoorPassword
                          ? ` / ${same.frontdoorPassword}`
                          : ""
                      }        
                    </div>
                    <div class="info-address">
                      ${
                        same.status === STATUS_PICKUP
                          ? `(출) ${
                              same.senderAddress || same.senderAddressRoad || ""
                            }, ${same.senderAddressDetail}`
                          : `(도) ${same.displayAddress} ${
                              same.memoFromCustomer
                                ? `<span class="c-red">(${same.memoFromCustomer})</span>`
                                : ""
                            }`
                      }
                    </div>
                    <div class="info-address bold">
                      ${
                        same.status !== STATUS_PICKUP &&
                        same.customerAddressEngGpt
                          ? `(GPT번역) ${same.customerAddressEngGpt}`
                          : ""
                      }
                    </div>
                    <div class="info-address">
                      ${
                        same.status === STATUS_PICKUP
                          ? `${
                              same.senderAddressRoadWithoutSigungu ||
                              same.senderAddressWithoutSigungu
                            } ${
                              same.senderAddressObj &&
                              same.senderAddressObj.building
                                ? ` ${same.senderAddressObj.building}`
                                : ""
                            }`
                          : `${
                              same.receiverAddressRoadWithoutSigungu ||
                              same.receiverAddressWithoutSigungu
                            }${
                              same.receiverAddressObj &&
                              same.receiverAddressObj.building
                                ? ` ${same.receiverAddressObj.building}`
                                : ""
                            }${
                              same.receiverAddressRoadWithoutSigungu &&
                              same.senderAddressWithoutSigungu
                                ? `/${same.senderAddressWithoutSigungu}`
                                : ""
                            }${
                              !same.receiverAddressRoadWithoutSigungu &&
                              same.receiverDong
                                ? `/${same.receiverDong}`
                                : ""
                            }`
                      }
                    </div>
                  </div>
                `;

              if (
                same.status === STATUS_PICKUP &&
                same.type === DELIVERY_TYPE_NORMAL
              ) {
                pickupInfoWindowDone = true;
              }
            }
          });
        }

        // 툴팁 : 동일 주소 목록(수거건) - 배송 출발 건만 표시됨
        if (item.samePlacesPickup && item.samePlacesPickup.length > 0) {
          item.samePlacesPickup.forEach((same) => {
            if (
              same.status === STATUS_DELIVERY_WAITING ||
              same.status === STATUS_DELIVERY_POSTPONED ||
              same.status === STATUS_DELIVERY_STARTED ||
              same.type === DELIVERY_TYPE_PICKUP ||
              same.type === DELIVERY_TYPE_RETURN ||
              !pickupInfoWindowDone
            ) {
              if (same.isHighPrice) {
                isHighPrice = true;
              }
              if (same.isLimitedHours) {
                isLimitedHours = true;
              }
              if (same.reallocated) {
                isReallocated = true;
              }

              let linkingDetail = `
                    window.ReactNativeWebView.postMessage('${JSON.stringify({
                      type: "detail",
                      bookId: same.bookId,
                    })}');
                  `;

              if (!appVersion) {
                linkingDetail = `window.ReactNativeWebView.postMessage('${same.bookId}');`;
              }

              let sameReceiverWaitDeliveriesArr =
                same.sameReceiverDeliveriesObj.waiting || [];
              let sameReceiverStartedDeliveriesArr =
                same.sameReceiverDeliveriesObj.started || [];
              let sameReceiverPostponedDeliveriesArr =
                same.sameReceiverDeliveriesObj.postponed || [];

              if (
                (same.status === STATUS_DELIVERY_WAITING ||
                  same.status === STATUS_DELIVERY_POSTPONED ||
                  same.status === STATUS_DELIVERY_STARTED) &&
                deliveriesAllocated &&
                deliveriesAllocated.length > 0
              ) {
                if (
                  sameReceiverWaitDeliveriesArr.length >
                  receiverWaitDeliveriesCount
                ) {
                  receiverWaitDeliveriesCount =
                    sameReceiverWaitDeliveriesArr.length;
                }
                if (
                  sameReceiverStartedDeliveriesArr.length >
                  receiverStartedDeliveriesCount
                ) {
                  receiverStartedDeliveriesCount =
                    sameReceiverStartedDeliveriesArr.length;
                }
                if (
                  sameReceiverPostponedDeliveriesArr.length >
                  receiverPostponedDeliveriesCount
                ) {
                  receiverPostponedDeliveriesCount =
                    sameReceiverPostponedDeliveriesArr.length;
                }
              }

              let sameOrderingNumberByDeliveryRiderId = "";

              if (same.orderingNumberByDeliveryRiderId) {
                orderingNumbersByDeliveryRiderId.push(
                  same.orderingNumberByDeliveryRiderId,
                );

                sameOrderingNumberByDeliveryRiderId = ` [${same.orderingNumberByDeliveryRiderId}]`;
              }

              infoWindowContent += `
                    <div 
                      class="info ${same.statusText} ${same.keyColor}" 
                      style="padding: 5px 10px 10px; max-width: 250px" 
                      onclick=${linkingDetail}
                    >
                      ${
                        same.status === STATUS_PICKUP
                          ? ""
                          : `
                        <div class="info-icons">
                          <div class="info-icon highPrice ${
                            !same.isHighPrice && "disabled"
                          }">
                            <img style="width: 6px;" src="${mapImages.iconDollar}" />
                          </div>
                          <div class="info-icon time ${
                            !same.isLimitedHours && "disabled"
                          }">
                            <img style="width: 6px;" src="${mapImages.iconClock}" />
                          </div>
                          <div class="info-icon rearrange ${
                            !same.reallocated && "disabled"
                          }">
                            <img style="width: 8px;" src="${mapImages.iconRotation}" />
                          </div>
                          <div class="info-icon deliveryWait ${
                            sameReceiverWaitDeliveriesArr.length === 0 &&
                            "disabled"
                          }">
                            ${sameReceiverWaitDeliveriesArr.length}
                          </div>
                          <div class="info-icon deliveryStart ${
                            sameReceiverStartedDeliveriesArr.length === 0 &&
                            "disabled"
                          }">
                            ${sameReceiverStartedDeliveriesArr.length}
                          </div>
                          <div class="info-icon deliveryPostponed ${
                            sameReceiverPostponedDeliveriesArr.length === 0 &&
                            "disabled"
                          }">
                            <img style="width: 7px;" src="${mapImages.iconPostponed}" />
                          </div>
                        </div>
                      `
                      }
                      <div class="info-title ${same.statusText}">
                        <span style="color: ${same.keyColorHex}">(${
                          same.statusString
                        })${sameOrderingNumberByDeliveryRiderId}</span>&nbsp;
                        ${same.bookId}&nbsp;
                        <span style="color: ${same.keyColorHex}">${
                          same.typeString
                        }</span>
                      </div>
                      <div class="info-spot">
                        ${
                          same.status === STATUS_PICKUP
                            ? `${same.spotName} ${
                                same.spot ? `(${same.renderSenderName})` : ""
                              }`
                            : `${same.renderReceiverName}(${same.renderSenderName})`
                        }
                        <span class="c-red">
                          ${
                            same.status !== STATUS_PICKUP &&
                            same.customerAddressEng
                              ? " (영어주소)"
                              : ""
                          }
                        </span>
                      </div>
                      <div class="info-base-address">
                        ${
                          same.type === DELIVERY_TYPE_NORMAL
                            ? `${
                                same.displayReceiverBuildingDongNumber || "없음"
                              }(${same.renderAddressApiType})`
                            : same.type !== DELIVERY_TYPE_NORMAL
                              ? `${
                                  same.displaySenderBuildingDongNumber || "없음"
                                }(${same.renderAddressApiType})`
                              : ""
                        } 
                        ${
                          same.frontdoorPassword
                            ? ` / ${same.frontdoorPassword}`
                            : ""
                        }        
                      </div>
                      <div class="info-address">
                        ${
                          same.status === STATUS_PICKUP
                            ? `(출) ${
                                same.senderAddress ||
                                same.senderAddressRoad ||
                                ""
                              }, ${same.senderAddressDetail}`
                            : `(도) ${same.displayAddress} ${
                                same.memoFromCustomer
                                  ? `<span class="c-red">(${same.memoFromCustomer})</span>`
                                  : ""
                              }`
                        }
                      </div>
                      <div class="info-address bold">
                        ${
                          same.status !== STATUS_PICKUP &&
                          same.customerAddressEngGpt
                            ? `(GPT번역) ${same.customerAddressEngGpt}`
                            : ""
                        }
                      </div>
                      <div class="info-address">
                        ${
                          same.status === STATUS_PICKUP
                            ? `${
                                same.senderAddressRoadWithoutSigungu ||
                                same.senderAddressWithoutSigungu
                              } ${
                                same.senderAddressObj &&
                                same.senderAddressObj.building
                                  ? ` ${same.senderAddressObj.building}`
                                  : ""
                              }`
                            : `${
                                same.receiverAddressRoadWithoutSigungu ||
                                same.receiverAddressWithoutSigungu
                              }${
                                same.receiverAddressObj &&
                                same.receiverAddressObj.building
                                  ? ` ${same.receiverAddressObj.building}`
                                  : ""
                              }${
                                same.receiverAddressRoadWithoutSigungu &&
                                same.senderAddressWithoutSigungu
                                  ? `/${same.senderAddressWithoutSigungu}`
                                  : ""
                              }${
                                !same.receiverAddressRoadWithoutSigungu &&
                                same.receiverDong
                                  ? `/${same.receiverDong}`
                                  : ""
                              }`
                        }
                      </div>
                    </div>
                  `;

              if (
                same.status === STATUS_PICKUP &&
                same.type === DELIVERY_TYPE_NORMAL
              ) {
                pickupInfoWindowDone = true;
              }
            }
          });
        }

        infoWindowContent += "</div>";

        orderingNumbersByDeliveryRiderId.sort((a, b) => (a > b ? 1 : -1));

        let displayInfos = [];
        if (isCustomerAddressEng) {
          displayInfos.push("영어");
        }
        if (isLimitedHours) {
          displayInfos.push("보장");
        }

        // 마커 아이콘
        let markerIcon = {
          size: new window.naver.maps.Size(23, 32),
          scaledSize: new window.naver.maps.Size(23, 32),
          content: `
              <div class="marker ${item.statusText} ${
                item.samePlacekeyColor || item.keyColor
              }">
                ${
                  isRearrangeMode
                    ? getRearrange(
                        isRearrangeMode,
                        selectedDeliveries,
                        item.bookId,
                      )
                    : getOrder(
                        true,
                        orderList,
                        newOrderList,
                        item.order,
                        item.bookId,
                        orderingNumbersByDeliveryRiderId,
                      )
                }
                
                <div class="marker-plus-wrap">
                  ${
                    item.samePlacesCount || item.samePlacesPickupCount
                      ? `<div class="marker-plus">
                        ${
                          item.samePlacesCount > 0
                            ? `+${item.samePlacesCount}`
                            : ""
                        }${
                          item.samePlacesPickupCount > 0
                            ? `(${item.samePlacesPickupCount})`
                            : ""
                        }</div>`
                      : ""
                  }
                  
                  ${
                    isNearbyMarker
                      ? `<div class="marker-near"><img style="width: 14px;" src="${mapImages.iconNear}" /></div>`
                      : ""
                  }
                  ${
                    isNearbyMarkerInOtherStatus
                      ? nearbyMarkersInOtherStatusArr.map(
                          (nearbyMarker, idx) =>
                            `<div class="marker-near delivery ${
                              nearbyMarker.status === STATUS_PICKUP &&
                              "deliveryPickup"
                            } ${
                              nearbyMarker.status === STATUS_DELIVERY_WAITING &&
                              "deliveryWait "
                            } ${
                              nearbyMarker.status === STATUS_DELIVERY_STARTED &&
                              "deliveryStart"
                            } ${
                              nearbyMarker.status ===
                                STATUS_DELIVERY_POSTPONED && "deliveryPostponed"
                            } ${nearbyMarker.status === STATUS_CS && "cs"}"
                        key=${idx}
                      >
                        <img style="width: 7px;" src="${mapImages.iconNearplus}" />
                      </div>`,
                        )
                      : ""
                  }
                </div>
                
                ${
                  item.status === STATUS_PICKUP
                    ? ``
                    : `
                  <div class="marker-icons ${
                    item.samePlacesCount ||
                    item.samePlacesPickupCount ||
                    isNearbyMarker ||
                    isNearbyMarkerInOtherStatus
                      ? "plus"
                      : ""
                  }">
                    ${
                      isHighPrice
                        ? `<div class="marker-icon highPrice"><img style="width: 6px;" src="${mapImages.iconDollar}" /></div>`
                        : ""
                    }
                    ${
                      isLimitedHours
                        ? `<div class="marker-icon time"><img style="width: 6px;" src="${mapImages.iconClock}" /></div>`
                        : ""
                    }
                    ${
                      isReallocated
                        ? `<div class="marker-icon rearrange"><img style="width: 8px;" src="${mapImages.iconRotation}" /></div>`
                        : ""
                    }
                    ${
                      receiverWaitDeliveriesCount > 0
                        ? `<div class="marker-icon deliveryWait"></div>`
                        : ""
                    }
                    ${
                      receiverStartedDeliveriesCount > 0
                        ? `<div class="marker-icon deliveryStart">${receiverStartedDeliveriesCount}</div>`
                        : ""
                    }
                    ${
                      receiverPostponedDeliveriesCount > 0
                        ? `<div class="marker-icon deliveryPostponed"><img style="width: 7px;" src="${mapImages.iconPostponed}" /></div>`
                        : ""
                    }
                  </div>
                `
                }
                
                ${
                  item.customerAddressEng ||
                  item.order ||
                  showMarkerAddress ||
                  orderingNumbersByDeliveryRiderId.length > 0
                    ? `<div class="marker-address">
                    ${
                      displayInfos.length > 0
                        ? `${displayInfos.join("/")}</br>`
                        : ""
                    }
                    ${
                      item.displayReceiverBuildingDongNumber
                        ? `(${item.displayReceiverBuildingDongNumber})</br>`
                        : ""
                    }
                    <div>
                      ${
                        orderingNumbersByDeliveryRiderId.length
                          ? `[스${orderingNumbersByDeliveryRiderId.join(
                              "] [스",
                            )}]`
                          : ""
                      }${
                        item.order
                          ? `[배${getOrder(
                              false,
                              orderList,
                              newOrderList,
                              item.order,
                              item.bookId,
                            )}]`
                          : ""
                      }
                    </div>
                  ${
                    showMarkerAddress
                      ? item.status === STATUS_PICKUP
                        ? `${
                            item.senderAddressRoadWithoutSigungu ||
                            item.senderAddressWithoutSigungu
                          }${
                            item.senderAddressObj &&
                            item.senderAddressObj.building
                              ? `<br/>${item.senderAddressObj.building}`
                              : ""
                          }`
                        : `${
                            item.receiverAddressRoadWithoutSigungu ||
                            item.receiverAddressWithoutSigungu
                          }${
                            item.receiverAddressObj &&
                            item.receiverAddressObj.building
                              ? `<br/>${item.receiverAddressObj.building}`
                              : ""
                          }`
                      : ""
                  }
                </div>`
                    : ""
                }
              </div>
            `,
        };

        // 마커
        let marker = new window.naver.maps.Marker({
          map: map,
          position: new window.naver.maps.LatLng(item.latitude, item.longitude),
          title: item.bookId,
          icon: markerIcon,
          zIndex: 100,
        });

        marker.status = item.status;
        marker.order = isRearrangeMode
          ? getRearrange(isRearrangeMode, selectedDeliveries, item.bookId)
          : getOrder(
              isOrderMode,
              orderList,
              newOrderList,
              item.order,
              item.bookId,
            );
        marker.bookId = item.bookId;
        marker.receiverAddress = item.receiverAddress;
        marker.samePlaces = item.samePlaces;
        marker.samePlacesPickup = item.samePlacesPickup;
        marker.statusString = item.statusString;
        marker.statusRearrangeKey = item.statusRearrangeKey;
        marker.reallocatedRiderId = item.reallocatedRiderId;

        // 툴팁
        let infoWindow = new window.naver.maps.InfoWindow({
          content: infoWindowContent,
        });

        markers.push(marker);
        infoWindows.push(infoWindow);
      });

      updateMarkers(map, markers);

      window.naver.maps.Event.addListener(map, "idle", function () {
        updateMarkers(map, markers);
      });

      for (var j = 0, jj = markers.length; j < jj; j++) {
        window.naver.maps.Event.addListener(
          markers[j],
          "click",
          getClickHandler(j),
        );
      }

      window.naver.maps.Event.addListener(map, "click", function () {
        for (var i = 0; i < markers.length; i++) {
          var infoWindow = infoWindows[i];

          if (infoWindow.getMap()) {
            infoWindow.close();
          }
        }
      });

      if (window.ReactNativeWebView) {
        if (appVersion) {
          window.ReactNativeWebView.postMessage(
            JSON.stringify({ type: MESSAGE_UPDATED }),
          );
        } else {
          window.ReactNativeWebView.postMessage(MESSAGE_UPDATED);
        }
      }
    } else {
      if (window.ReactNativeWebView) {
        if (appVersion) {
          window.ReactNativeWebView.postMessage(
            JSON.stringify({ type: MESSAGE_UPDATED }),
          );
        } else {
          window.ReactNativeWebView.postMessage(MESSAGE_UPDATED);
        }
      }
    }
  }, [
    getClickHandler,
    map,
    markerList,
    markerListWithSameplace,
    orderList,
    newOrderList,
    isOrderMode,
    selectedDeliveries,
    isRearrangeMode,
    updateMarkers,
    linkOthers,
    appVersion,
    deliveriesAllocated,
    showMarkerAddress,
  ]);

  const fetchPolyline = useCallback(async () => {
    try {
      // 최신 배송완료건 조회
      const _delvieryCompleted = await deliveriesActions.fetchCompleted();

      // 다음 배송건 조회
      const nextOrderList = orderList;
      // const nextOrderList = _delvieryCompleted?.order
      // ? orderList.filter((e) => e.order > _delvieryCompleted.order)
      // : orderList;

      if (nextOrderList.length > 0) {
        const deliveryNext = deliveryStartList.find(
          (e) => e.bookId === nextOrderList[0].bookId,
        );

        const start = {
          longitude:
            _delvieryCompleted?.compareReceiverLocation?.longitude ||
            routesViaStops?.start[0],
          latitude:
            _delvieryCompleted?.compareReceiverLocation?.latitude ||
            routesViaStops?.start[1],
        };

        if (
          start.longitude &&
          start.latitude &&
          deliveryNext.compareReceiverLocation?.longitude &&
          deliveryNext.compareReceiverLocation?.latitude
        ) {
          await routesActions.fetchRoutes({
            delvieryCompleted: start,
            deliveryNext,
            start: [Number(start.longitude), Number(start.latitude)],
            goal: [
              Number(deliveryNext.compareReceiverLocation?.longitude),
              Number(deliveryNext.compareReceiverLocation?.latitude),
            ],
          });
        }
      }
    } catch (e) {
      console.log(e);
    }
  }, [
    deliveriesActions.fetchCompleted,
    routesActions.fetchRoutes,
    orderList,
    routesViaStops,
  ]);

  const drawPolyline = useCallback(() => {
    for (let i = 0; i < polylines.length; i++) {
      polylines[i].setMap(null);
    }

    if (markerCompleted) {
      markerCompleted.setMap(null);
    }

    const path = route?.path;

    let polylinePath = [];
    path.forEach((p) => {
      polylinePath.push(new window.naver.maps.LatLng(p[1], p[0]));
    });

    //위의 배열을 이용해 라인 그리기
    const polyline = new window.naver.maps.Polyline({
      path: polylinePath, //선 위치 변수배열
      strokeColor: "#FF0000", //선 색 빨강 #빨강,초록,파랑
      strokeOpacity: 0.8, //선 투명도 0 ~ 1
      strokeWeight: 6, //선 두께
      map: map, //오버레이할 지도
    });

    const markerIcon = {
      size: new window.naver.maps.Size(23, 32),
      scaledSize: new window.naver.maps.Size(23, 32),
      content: `<div class="marker completed-route" />`,
    };

    polylines.push(polyline);

    // 배송완료건 마커
    if (delvieryCompleted?.latitude && delvieryCompleted?.longitude) {
      markerCompleted = new window.naver.maps.Marker({
        position: new window.naver.maps.LatLng(
          delvieryCompleted.latitude,
          delvieryCompleted.longitude,
        ),
        map: map,
        icon: markerIcon,
      });
    }
  }, [map, polylines, markerCompleted, route, delvieryCompleted]);

  useEffect(() => {
    if (map && fetched) {
      // if (window.ReactNativeWebView && !orderMode) {
      //   window.ReactNativeWebView.postMessage("updating");
      // }

      drawMarker();
    }

    return () => {
      if (window.naver) {
        window.naver.maps.Event.removeListener();
      }
    };
  }, [drawMarker, fetched, isOrderMode, map, markerList]);

  useEffect(() => {
    if (map && fetched && routesViaStops && orderList) {
      fetchPolyline();
    }
  }, [fetchPolyline, fetched, map, routesViaStops, orderList]);

  useEffect(() => {
    if (route?.path) {
      drawPolyline();
    }
  }, [route]);

  function showMarker(map, marker) {
    if (marker.setMap()) return;
    marker.setMap(map);
  }

  function hideMarker(marker) {
    if (!marker.setMap()) return;
    marker.setMap(null);
  }

  // 배송 순서 지정
  const handleClickOrderDeliveries = useCallback(() => {
    if (deliveryStartList.length !== newOrderList.length) {
      openModal(
        <OrderConfirmModal
          key="order-confirm-modal"
          changeOrder={submitOrderDeliveries}
        />,
      );
    } else {
      submitOrderDeliveries();
    }
  }, [deliveryStartList, newOrderList]);

  const submitOrderDeliveries = useCallback(async () => {
    try {
      let _orderList = [...newOrderList];

      deliveryStartList.forEach((deliver) => {
        let index = _orderList.findIndex((el) => el.bookId === deliver.bookId);
        if (index < 0)
          _orderList.push({
            bookId: deliver.bookId,
            order: null,
          });
      });

      const data = {
        orders: _orderList,
      };

      await orderActions.reorderDeliveries(data);

      toast(`순서 지정을 저장하였습니다.`, {
        className: "toast-background",
        bodyClassName: "toast-body",
        progressClassName: "fancy-progress-bar",
      });

      cancelOrderMode();

      handleChangeReload(true);
    } catch (e) {
      alert(`순서 지정에 실패하였습니다.. \n에러메시지: ${e.message}`);
    }
  }, [orderActions.reorderDeliveries, newOrderList, deliveryStartList]);

  // 재배차 모달
  const handleClickRearrangeDeliveries = useCallback(() => {
    if (selectedDeliveries.length > 0) {
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({ type: "favoRiders" }),
        );
      }

      openModal(
        <RidersModal
          key="riders-modal"
          onSubmitAfter={handleChangeReload}
          favoRiders={favoRiders}
        />,
      );
    } else {
      toast(`재배차할 배송건을 선택해주세요.`, {
        className: "toast-background",
        bodyClassName: "toast-body",
        progressClassName: "fancy-progress-bar",
      });
    }
  }, [selectedDeliveries]);

  // 배송 대기 시작
  const handleClickPrevDeliveries = useCallback(async () => {
    let _prevDeliveries = [];
    let _status = null;
    selectedDeliveries.forEach((el) => {
      if (
        el.status === STATUS_DELIVERY_STARTED ||
        el.status === STATUS_DELIVERY_POSTPONED
      ) {
        _prevDeliveries.push(el.bookId);
        _status = el.status;
      }
    });

    if (_prevDeliveries.length > 0) {
      if (
        window.confirm(
          `${_prevDeliveries[0]} 포함 ${_prevDeliveries.length}건을 배송대기 상태로 변경하시겠습니까?`,
        )
      ) {
        try {
          await deliveriesActions.prevDeliveries({
            bookIds: _prevDeliveries,
            status: _status === STATUS_DELIVERY_STARTED ? 4 : 12,
          });

          toast(
            `${_prevDeliveries[0]} 포함 ${_prevDeliveries.length}건을 배송대기 상태로 변경처리하였습니다.`,
            {
              className: "toast-background",
              bodyClassName: "toast-body",
              progressClassName: "fancy-progress-bar",
            },
          );

          reallocationActions.cancelRearrangeMode();
          handleChangeReload(true);
        } catch (e) {
          alert(
            `배송대기 상태로 변경에 실패하였습니다. \n에러메시지: ${e.message}`,
          );
        }
      }
    } else {
      toast(`대기 상태로 변경할 배송건을 선택해주세요.`, {
        className: "toast-background",
        bodyClassName: "toast-body",
        progressClassName: "fancy-progress-bar",
      });
    }
  }, [
    selectedDeliveries,
    deliveriesActions.prevDeliveries,
    reallocationActions.cancelRearrangeMode,
  ]);

  // 배송 연기 시작
  const handleClickPostponedDeliveries = useCallback(async () => {
    let _prevDeliveries = [];
    let _status = null;
    selectedDeliveries.forEach((el) => {
      if (
        el.status === STATUS_DELIVERY_STARTED ||
        el.status === STATUS_DELIVERY_WAITING
      ) {
        _prevDeliveries.push(el.bookId);
        _status = el.status;
      }
    });

    if (_prevDeliveries.length > 0) {
      if (
        window.confirm(
          `${_prevDeliveries[0]} 포함 ${_prevDeliveries.length}건을 배송연기 상태로 변경하시겠습니까?`,
        )
      ) {
        try {
          await Promise.all(
            _prevDeliveries.map((bookId) =>
              deliveriesActions.setPostponed(bookId),
            ),
          );

          toast(
            `${_prevDeliveries[0]} 포함 ${_prevDeliveries.length}건을 배송연기 상태로 변경처리하였습니다.`,
            {
              className: "toast-background",
              bodyClassName: "toast-body",
              progressClassName: "fancy-progress-bar",
            },
          );

          reallocationActions.cancelRearrangeMode();
          handleChangeReload(true);
        } catch (e) {
          alert(
            `배송연기 상태로 변경에 실패하였습니다. \n에러메시지: ${e.message}`,
          );
        }
      }
    } else {
      toast(`연기 상태로 변경할 배송건을 선택해주세요.`, {
        className: "toast-background",
        bodyClassName: "toast-body",
        progressClassName: "fancy-progress-bar",
      });
    }
  }, [
    selectedDeliveries,
    deliveriesActions.setPostponed,
    reallocationActions.cancelRearrangeMode,
  ]);

  // 지도 설정
  const handleClickSetting = useCallback(() => {
    openModal(
      <SettingModal
        key="setting-modal"
        linkOthers={linkOthers}
        statuses={statuses}
        currentCenter={currentCenter}
        showHideMarkers={showHideMarkers}
        showMarkerAddress={showMarkerAddress}
        onSubmit={updateSetting}
      />,
    );
  }, []);

  const updateSetting = useCallback(
    (data) => {
      deliveriesActions.setStatuses(data.statuses);
      mapActions.setLinkOthers(data.linkOthers);
      mapActions.getCurrentPosition(data.currentCenter);
      mapActions.setShowHideMarkers(data.showHideMarkers === "true");
      mapActions.setShowMarkerAddress(data.showMarkerAddress);

      closeModal();
    },
    [
      currentCenter,
      deliveriesActions.setStatuses,
      mapActions.getCurrentPosition,
      mapActions.setShowHideMarkers,
      mapActions.setShowMarkerAddress,
      mapActions.setLinkOthers,
    ],
  );

  // 현재 위치 이동
  const handleClickCurrent = useCallback(() => {
    mapActions.setMapCurrentCenter();
  }, [mapActions.setMapCurrentCenter]);

  return (
    <ThemeProvider theme={size ? SIZE[size] : {}}>
      <MapContainer>
        <div className="map">
          {/** 배송 순서 클릭 가이드 */}
          <OrderGuide
            isShow={isOrderMode && !modals.length}
            orderListCount={newOrderList.length}
            deliveriesCount={deliveryStartList.length}
            onClickSubmit={handleClickOrderDeliveries}
            onClickCancel={handleClickCancelOrderMode}
          />

          {/** 재배차 클릭 가이드 */}
          <RearrangeGuide
            isShow={isRearrangeMode}
            rearrangeCount={selectedDeliveries.length}
            deliveriesCount={
              selectedDeliveries.length > 0
                ? selectedDeliveries[0].status === STATUS_PICKUP
                  ? count.pickups
                  : selectedDeliveries[0].status === STATUS_DELIVERY_WAITING
                    ? count.deliveriesBeforeStart
                    : selectedDeliveries[0].status === STATUS_DELIVERY_POSTPONED
                      ? count.deliveriesPostponed
                      : count.deliveriesStarted
                : count.pickups + count.deliveriesAllocated
            }
            onClickSubmitRearrange={handleClickRearrangeDeliveries}
            onClickSubmitPrev={handleClickPrevDeliveries}
            onClickSubmitPostponed={handleClickPostponedDeliveries}
            onClickCancel={handleClickCancelRearrangeMode}
          />

          <TopWrap hide={modals.length > 0 || isOrderMode || isRearrangeMode}>
            <CountSummary count={count} limitedHoursCount={limitedHoursCount} />
          </TopWrap>

          <BottomWrap
            hide={modals.length > 0 || isOrderMode || isRearrangeMode}
          >
            <BottomSummary
              statuses={statuses}
              linkOthers={linkOthers}
              currentCenter={currentCenter}
              showHideMarkers={showHideMarkers}
              showMarkerAddress={showMarkerAddress}
            />
            <BottomButtonsWrap>
              {/** 설정 */}
              <ButtonSetting onClick={handleClickSetting}>
                <IconSetting />
              </ButtonSetting>
              {/** 지도 하단 : 순서지정/재배차 버튼 */}
              <BottomButtons
                onClickStartOrder={handleClickSelectOrderAuto}
                onClickStartRearrange={handleClickStartRearrange}
              />
              {/** 현재위치 */}
              <ButtonCurrent onClick={handleClickCurrent}>
                <IconCurrent />
              </ButtonCurrent>
              {/** 새로고침 */}
              <ButtonReload onClick={reloadDeliveries}>
                <IconRefresh />
              </ButtonReload>
            </BottomButtonsWrap>
          </BottomWrap>

          {map && showHideMarkers && (
            <TrackingMarkers
              markers={hideMarkers}
              mapBounds={map.getBounds()}
              withRoute={route}
            />
          )}

          {/** 추천 경로 관련 내용 표시 */}
          <RouteInfo />

          <MapConatiner id="map" />
        </div>
      </MapContainer>
    </ThemeProvider>
  );
}

export default MapWithRoutes;
